import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'
import { DirectoryModal } from '../../../../i18n'
import { LOADING_STRING } from '../../../NonReact/constants/Common'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
// Language
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE, getAllTelServersAsSelectOptions, getPartitionsForTelServer } from '../../Common/API/ItemsGetters'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../Common/Components/FormRow'
import { ModalLoading } from '../../Common/Components/ModalLoading'
import { SelectOptions } from '../../Common/Components/Selects'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ReactState } from '../../Common/Contexts'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { handleUnknownForAvailables, removeUnknownOption } from '../../Common/Functions/CommonFunction'
import { FilterDefaults } from '../../Common/Functions/FilterDefaults'
import {
    DESCRIPTION_SCOPE_NAME,
    PARTITION_SCOPE_NAME,
    TEL_NUMBER_SCOPE_NAME,
    TEL_SERVER_SCOPE_NAME
} from '../Constants/FieldNames'
import { FilterValues } from '../Types/Types'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'

type Props = {
    filters?: FilterValues
    viewOnly: boolean | undefined
}

type DefaultAvailables = [SelectOptions<number>, SelectOptions<number>]

/**
 * Get the available entities defaults.
 *
 * @param scope - Form permissions from the server.
 *
 * @returns - An array of defaults.
 */
const getAvailableEntitiesDefaults = (filters?: FilterValues): DefaultAvailables => [
    [
        {
            label: LOADING_STRING,
            value: !IsVarEmpty(filters?.telSysID) ? parseInt(`${filters?.telSysID}`, 10) : ALL_VALUE
        }
    ],
    [
        {
            label: LOADING_STRING,
            value: filters?.partitionID ?? ALL_VALUE
        }
    ]
]

const DirectoryDetails: FC<Props> = ({ filters, viewOnly }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(DirectoryModal.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)

    const [telSysDefaults, partitionDefaults]: DefaultAvailables = getAvailableEntitiesDefaults(filters)

    const [availableTelServers, setAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(telSysDefaults)

    const [availablePartitions, setAvailablePartitions]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(partitionDefaults)

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)

    const isLoading: boolean = isLoadingTelServers || isLoadingPartitions

    const filterDefaults: FilterDefaults = FilterDefaults.getInstance(language)

    // Contexts
    const { watch, setValue }: UseFormMethods = useFormContext()

    const telServerValue: number = watch(TEL_SERVER_SCOPE_NAME)
    const partitionValue: number = watch(PARTITION_SCOPE_NAME)

    useEffect((): void => {
        setIsLoadingTelServers(true)
        getAllTelServersAsSelectOptions()
            .then((telServers: SelectOptions<number>): void => {
                setIsLoadingTelServers(false)
                handleUnknownForAvailables(telServers, setAvailableTelServers, telServerValue)
            })
            .catch((err: Error): void => {
                setIsLoadingTelServers(false)
                displayError(err)
            })
    }, [])

    useEffect((): void => {
        if (!IsVarEmpty(telServerValue) && telServerValue !== ALL_VALUE) {
            setIsLoadingPartitions(true)
            getPartitionsForTelServer(telServerValue)
                .then((partitions: SelectOptions<number>): void => {
                    setIsLoadingPartitions(false)
                    handleUnknownForAvailables(partitions, setAvailablePartitions, partitionValue)
                })
                .catch((err: Error): void => {
                    setIsLoadingPartitions(false)
                    displayError(err)
                })
        } else {
            handleUnknownForAvailables([], setAvailablePartitions, partitionValue)
        }

        removeUnknownOption(setAvailablePartitions, telServerValue)
    }, [telServerValue])

    if (isLoading) {
        return <ModalLoading tabs={1} sections={4} />
    }

    return (
        <div className='user-details-tab tab-area'>
            <FormRow>
                <ComboBox
                    disabled={viewOnly}
                    name={TEL_SERVER_SCOPE_NAME}
                    label={language.get(DirectoryModal.TelServerFormLabel)}
                    defaultOption={filterDefaults.getAllTelServersOption()}
                    options={availableTelServers}
                    loading={isLoadingTelServers}
                    noChange={isLoading}
                    onChange={(): void => {
                        setValue(PARTITION_SCOPE_NAME, ALL_VALUE)
                    }}
                    variant='outlined'
                />
            </FormRow>
            <FormRow>
                <ComboBox
                    disabled={viewOnly}
                    name={PARTITION_SCOPE_NAME}
                    label={language.get(DirectoryModal.PartitionFormLabel)}
                    defaultOption={filterDefaults.getAnyPartitionOption()}
                    options={availablePartitions}
                    loading={isLoadingPartitions}
                    noChange={isLoading}
                    multiple={false}
                    variant='outlined'
                />
            </FormRow>
            <FormRow>
                <ValidationErrors name={TEL_NUMBER_SCOPE_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(DirectoryModal.TelNumberFormLabel)}
                        inputName={TEL_NUMBER_SCOPE_NAME}
                        rules={{
                            required: language.get(DirectoryModal.TelNumberFormLabel_Validation_Error)
                        }}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <ValidationErrors name={DESCRIPTION_SCOPE_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(DirectoryModal.DescriptionFormLabel)}
                        inputName={DESCRIPTION_SCOPE_NAME}
                        rules={{
                            required: language.get(DirectoryModal.DescriptionFormLabel_Validation_Error)
                        }}
                    />
                </ValidationErrors>
            </FormRow>
        </div>
    )
}

export default DirectoryDetails
